<script setup>
import { useI18n } from 'vue-i18n';
import { getI18nKey } from 'dashboard/routes/dashboard/settings/helper/settingsHelper';
import {
  CUSTOM_ROLE_PERMISSION_GROUP_LOOKUP,
  CUSTOM_ROLE_HIDDEN_PERMISSIONS,
  sortCustomRoleGroupKeys,
  sortCustomRolePermissions,
} from 'dashboard/constants/permissions.js';

import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  roles: {
    type: Array,
    required: true,
  },
  loading: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();

const getGroupedPermissions = role => {
  const grouped = {};
  role.permissions
    .filter(permission => !CUSTOM_ROLE_HIDDEN_PERMISSIONS.includes(permission))
    .forEach(permission => {
      const groupKey =
        CUSTOM_ROLE_PERMISSION_GROUP_LOOKUP[permission] || 'SETTINGS';
      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(permission);
    });

  return sortCustomRoleGroupKeys(Object.keys(grouped)).map(groupKey => ({
    key: groupKey,
    label: t(`CUSTOM_ROLE.PERMISSION_GROUPS.${groupKey}.TITLE`),
    permissions: sortCustomRolePermissions(grouped[groupKey]).map(permission =>
      t(getI18nKey('CUSTOM_ROLE.PERMISSIONS', permission))
    ),
  }));
};
</script>

<template>
  <tbody class="cr-tbody">
    <tr v-for="(customRole, index) in roles" :key="index" class="cr-row">
      <td class="cr-td cr-td--name">
        <span class="cr-name" :title="customRole.name">{{
          customRole.name
        }}</span>
      </td>
      <td class="cr-td cr-td--desc">
        <span class="cr-desc">{{ customRole.description || '—' }}</span>
      </td>
      <td class="cr-td cr-td--perms">
        <div class="cr-perms-list">
          <div
            v-for="group in getGroupedPermissions(customRole)"
            :key="group.key"
            class="cr-perm-badge"
          >
            <span class="cr-perm-badge__title">{{ group.label }}</span>
            <span class="cr-perm-badge__items">{{
              group.permissions.join(', ')
            }}</span>
          </div>
        </div>
      </td>
      <td class="cr-td cr-td--actions">
        <div class="cr-actions">
          <Button
            v-tooltip.top="$t('CUSTOM_ROLE.EDIT.BUTTON_TEXT')"
            icon="i-lucide-pen"
            slate
            xs
            faded
            @click="emit('edit', customRole)"
          />
          <Button
            v-tooltip.top="$t('CUSTOM_ROLE.DELETE.BUTTON_TEXT')"
            icon="i-lucide-trash-2"
            xs
            ruby
            faded
            :is-loading="loading[customRole.id]"
            @click="emit('delete', customRole)"
          />
        </div>
      </td>
    </tr>
  </tbody>
</template>

<style scoped>
.cr-tbody {
}

.cr-row {
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.12s ease;
}
.cr-row:last-child {
  border-bottom: none;
}
.cr-row:hover {
  background: #fafafa;
}

/* Shared cell */
.cr-td {
  padding: 14px 24px;
  vertical-align: middle;
  color: #111827;
  font-size: 13px;
}

/* Name column */
.cr-td--name {
  width: 180px;
}
.cr-name {
  display: block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  color: #111827;
}

/* Description column */
.cr-td--desc {
  max-width: 260px;
}
.cr-desc {
  color: #4b5563;
  font-size: 13px;
}

/* Permissions column */
.cr-td--perms {
}
.cr-perms-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.cr-perm-badge {
  display: inline-flex;
  flex-direction: column;
  padding: 5px 10px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 11px;
}
.cr-perm-badge__title {
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}
.cr-perm-badge__items {
  color: #6b7280;
  line-height: 1.4;
}

/* Actions column */
.cr-td--actions {
  width: 90px;
}
.cr-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}
</style>
